import { AxiosResponse, CancelToken } from "axios";
import Model from '../models/Model'
import apiService from "./ApiService";
import ModelResponse from '@services/apiResponse/ModelResponse';
import ModelCollectionResponse from '@services/apiResponse/ModelCollectionResponse';
import PaginateResponse from '@services/apiResponse/PaginateResponse';
import HttpResponse from "@services/apiResponse/HttpResponse";

export default class EntityManager {

  static async all<T extends Model>(modelClass: any, options: EntityManagerOption = {}): Promise<ModelCollectionResponse<T>> {
    let fullpath = EntityManager.getPath(modelClass, options.parentModel, options.path);
    let params = EntityManager.buildParam(options.params, options);
    let response: AxiosResponse;
    try {
      response = await apiService.get(fullpath, params);
    } catch (error) {
      response = error.response;
    }
    return new ModelCollectionResponse<T>(response, {modelClass});
  }

  static async allWithPaginate<T extends Model>(modelClass: any, options: EntityManagerOption = {}): Promise<PaginateResponse<T>> {
    let fullpath = EntityManager.getPath(modelClass, options.parentModel, options.path);
    let params = EntityManager.buildParam(options.params, options);
    let response: AxiosResponse;
    try {
      response = await apiService.get(fullpath, {...params, paginate: true});
    } catch (error) {
      response = error.response;
    }
    return new PaginateResponse<T>(response, {modelClass});
  }

  static async show<T extends Model>(modelClass: any, id, options: EntityManagerOption = {}): Promise<ModelResponse<T>> {
    let fullpath = EntityManager.getPath(modelClass, options.parentModel, options.path);
    let params = EntityManager.buildParam(options.params, options);
    let response: AxiosResponse;
    try {
      response = await apiService.get(`${fullpath}/${id}`, params);
    } catch (error) {
      response = error.response;
    }
    return new ModelResponse<T>(response, {modelClass});
  }

  static async create<T extends Model>(model: T, options: CreateOptions = {}): Promise<ModelResponse<T>> {
    let fullpath = EntityManager.getPath(model.constructor, options.parentModel);
    let param = EntityManager.buildParam(model.getApiParam({only: options.only}), options);
    let response: AxiosResponse;
    try {
      response = await apiService.post(fullpath, param, {multipart: options.multipart});
    } catch (error) {
      response = error.response;
    }
    return new ModelResponse<T>(response, {modelClass: model.constructor});
  }

  static async update<T extends Model>(model: T, options: UpdateOptions = {}): Promise<ModelResponse<T>> {
    let param = EntityManager.buildParam(model.getApiParam({only: options.only}), options);
    // let param = EntityManager.buildParam(model.getApiParam({only: options.only, dirty: true}), options);
    let path;
    if (param instanceof FormData) {
      param.append('_method', 'PUT');
      path = param.get('id')
    } else {
      path = param.id;
      param['_method'] = 'PUT'
    }
    let fullpath = EntityManager.getPath(model.constructor, options.parentModel, path);
    let response: AxiosResponse;
    try {
      response = await apiService.post(fullpath, param, {multipart: options.multipart})
    } catch (error) {
      response = error.response;
    }
    return new ModelResponse<T>(response, {modelClass: model.constructor});
  }

  static async delete<T extends Model>(model: T, options: DeleteOptions = {}): Promise<ModelResponse<T>> {
    let response: AxiosResponse;
    try {
      let fullpath = EntityManager.getPath(model.constructor, options.parentModel, model.id);
      response = await apiService.delete(fullpath)
    } catch (error) {
      response = error.response;
    }
    return new ModelResponse<T>(response, {modelClass: model.constructor});
  }

  static async deleteMany<T extends Model>(modelClass, options: DeleteOptions = {}): Promise<ModelResponse<T>> {
    let fullpath = EntityManager.getPath(modelClass, options.parentModel, "all");
    let response: AxiosResponse;
    try {
      response = await apiService.delete(fullpath, {modelIds: options.params.map(m => m.id)})
    } catch (error) {
      response = error.response;
    }
    return new ModelResponse<T>(response, {modelClass});
  }

  static createMany<T extends Model>(modelClass, data: any[]): Promise<ModelCollectionResponse<T>> {
    return EntityManager.postCollection<T>(modelClass, {params: {models: data.map(d => d.getApiParam())}, path: "all"});
  }

  static async updateMany<T extends Model>(modelClass, options: UpdateOptions = {}): Promise<any> {
    let param = {models: options.params};
    param.models = param.models.map((m) => m.getApiParam({only: options.only}))
    param = EntityManager.buildParam(param, options);
    if (param instanceof FormData) {
      param.append('_method', 'PUT');
    } else {
      param['_method'] = 'PUT'
    }
    let fullpath = EntityManager.getPath(modelClass, options.parentModel, "all");
    let response: AxiosResponse;
    try {
      response = await apiService.post(fullpath, param, {multipart: options.multipart})
    } catch (error) {
      response = error.response;
    }
    return new ModelResponse<T>(response, {modelClass});
  }

  static async getDirect(modelClass: any, options: EntityManagerOption = {}) {
    let response: AxiosResponse;
    try {
      let fullpath = EntityManager.getPath(modelClass, options.parentModel, options.path);
      response = await apiService.get(fullpath, options.params)
    } catch (error) {
      response = error.response;
    }
    return new HttpResponse(response);
  }

  static async get<T extends Model>(modelClass: any, options: EntityManagerOption = {}) {
    let response: AxiosResponse;
    try {
      let fullpath = EntityManager.getPath(modelClass, options.parentModel, options.path);
      response = await apiService.get(fullpath, options.params)
    } catch (error) {
      response = error.response;
    }
    return new ModelResponse<T>(response, {modelClass});
  }

  static async getCollection<T extends Model>(modelClass: any, options: EntityManagerOption = {}) {
    let response: AxiosResponse;
    try {
      let fullpath = EntityManager.getPath(modelClass, options.parentModel, options.path);
      response = await apiService.get(fullpath, options.params)
    } catch (error) {
      response = error.response;
    }
    return new ModelCollectionResponse<T>(response, {modelClass});
  }

  static async postDirect(modelClass: any, options: EntityManagerOption = {}) {
    let response: AxiosResponse;
    try {
      let fullpath = EntityManager.getPath(modelClass, options.parentModel, options.path);
      response = await apiService.post(fullpath, options.params)
    } catch (error) {
      response = error.response;
    }
    return new HttpResponse(response);
  }

  static async post<T extends Model>(modelClass: any, options: EntityManagerOption = {}) {
    let response: AxiosResponse;
    try {
      let fullpath = EntityManager.getPath(modelClass, options.parentModel, options.path);
      response = await apiService.post(fullpath, options.params)
    } catch (error) {
      response = error.response;
    }
    return new ModelResponse<T>(response, {modelClass});
  }

  static async postCollection<T extends Model>(modelClass: any, options: EntityManagerOption = {}) {
    let response: AxiosResponse;
    try {
      let fullpath = EntityManager.getPath(modelClass, options.parentModel, options.path);
      response = await apiService.post(fullpath, options.params)
    } catch (error) {
      response = error.response;
    }
    // if (options.direct) return new HttpResponse(response);
    return new ModelCollectionResponse<T>(response, {modelClass});
  }

  static buildParam(params, options: EntityManagerOption) {
    if (options.merge) params = Object.assign(params, options.merge);
    if (options.with) params = Object.assign(params, {_with: options.with});
    return params;
  }

  static toCamelCase(json) {
    return json;
  }

  static toModel(modelClass, json) {
    return new modelClass(EntityManager.toCamelCase(json));
  }

  static toModels(modelClass, array: any[]) {
    return array.map(json => new modelClass(EntityManager.toCamelCase(json)));
  }

  static getPath(modelClass: any, parentModel?: any, path?: string): string {
    let fullpath = modelClass.modelUrl
    if (parentModel) {
      const parentUrl = parentModel.constructor?.modelUrl;
      fullpath = `${parentUrl}/${parentModel.id}/${fullpath}`;
    }
    if (path) {
      fullpath += `/${path}`
    }
    return fullpath;
  }

  static displayError(response: AxiosResponse) {
  }

}

export interface EntityManagerOption {
  parentModel?: any
  params?: any
  path?: string
  toaster?: boolean
  with?: string[]
  only?: string[]
  merge?: Object
  multipart?: boolean
  cancelToken?: CancelToken
}

export interface CreateOptions extends EntityManagerOption {
}

export interface UpdateOptions extends EntityManagerOption {
}

export interface DeleteOptions extends EntityManagerOption {
  confirmMessage?: string
}
